import React, { FunctionComponent, useEffect, useState } from 'react';
import { Dimmer, DimmerDimmable, Label, Menu, MenuItem, MenuItemProps, Segment } from 'semantic-ui-react';
import { CustomObject } from '../../../types';
import { useRest } from '../../../hooks';

import Chart from 'react-apexcharts';

type TimeSeriesData = {
    all: {
        color: string;
        data?: any[];
        title?: string;
    };
    received: {
        color: string;
        data?: any[];
        title?: string;
    };
    sent: {
        color: string;
        data?: any[];
        title?: string;
    };
};

type Props = {
    selectedWabaId: string;
};

const MessagesTimeSeries: FunctionComponent<Props> = ({ selectedWabaId }) => {
    const { consume } = useRest();

    const [noData, setNoData] = useState(false);
    const [selectedMode, setSelectedMode] = useState<'all' | 'received' | 'sent'>('all');

    const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData>({
        all: {
            color: '#1678C2',
            data: [],
            title: 'Mensanges',
        },
        received: {
            color: '#FF0000',
            data: [],
            title: 'Mensagens Recebidas',
        },
        sent: {
            color: '#EAAE00',
            data: [],
            title: 'Mensagens Enviadas',
        },
    });

    const handleOnMenuClick = async (_event: React.MouseEvent<HTMLAnchorElement>, { name }: MenuItemProps) => {
        if (name === 'all') {
            timeSeriesData.all.data = await getMessagesTimeSeries();
        } else if (name === 'sent') {
            timeSeriesData.sent.data = await getSentMessagesTimeSeries();
        } else if (name === 'received') {
            timeSeriesData.received.data = await getReceivedMessagesTimeSeries();
        }

        setSelectedMode(name as any);
        setTimeSeriesData({ ...timeSeriesData });
    };

    const getMessagesTimeSeries = async () => {
        const series = await consume<CustomObject>('get', `business/insights/time_series`, {
            waba_id: selectedWabaId,
        });

        return toTwoDimensionalVector(sortSeriesByDate(series));
    };

    const getSentMessagesTimeSeries = async () => {
        const series = await consume<CustomObject[]>('get', `business/insights/time_series/sent_messages`, {
            waba_id: selectedWabaId,
        });

        return toTwoDimensionalVector(sortSeriesByDate(series));
    };

    const getReceivedMessagesTimeSeries = async () => {
        const series = await consume<CustomObject[]>('get', `business/insights/time_series/received_messages`, {
            waba_id: selectedWabaId,
        });

        return toTwoDimensionalVector(sortSeriesByDate(series));
    };

    const sortSeriesByDate = (series: CustomObject) => {
        return Object.fromEntries(
            Object.entries(series).sort(([dateA], [dateB]) => {
                return new Date(dateB).getTime() - new Date(dateA).getTime();
            }),
        );
    };

    const toTwoDimensionalVector = (data: CustomObject) => {
        const seriesData = Object.entries(data).map(([date, count]) => ({
            x: new Date(date).toISOString(),
            y: count,
        }));
        return seriesData;
    };

    useEffect(() => {
        (async () => {
            if (selectedWabaId) {
                const data = await getMessagesTimeSeries();

                if (data.length <= 0) {
                    setNoData(true);
                    return;
                }

                timeSeriesData.all.data = data;
                setNoData(false);
                setSelectedMode('all');
                setTimeSeriesData({ ...timeSeriesData });
            }
        })();
    }, [selectedWabaId]);

    return (
        <Segment style={{ margin: 0, padding: '10px 12px' }}>
            <DimmerDimmable dimmed={noData} style={{ padding: 0, margin: 0, display: 'contents' }}>
                {noData && (
                    <Dimmer inverted active>
                        <div>
                            <Label content='Ainda não há dados para exibir' size='large' color='grey' basic />
                        </div>
                    </Dimmer>
                )}

                <Segment basic style={{ padding: 0, margin: '0 0 14px 0' }}>
                    <Menu borderless compact secondary>
                        <MenuItem
                            color='blue'
                            name='all'
                            active={selectedMode === 'all'}
                            content='Todas'
                            onClick={handleOnMenuClick}
                        />
                        <MenuItem
                            name='sent'
                            color='blue'
                            active={selectedMode === 'sent'}
                            content='Enviadas'
                            onClick={handleOnMenuClick}
                        />

                        <MenuItem
                            name='received'
                            color='blue'
                            content='Recebidas'
                            active={selectedMode === 'received'}
                            onClick={handleOnMenuClick}
                        />
                    </Menu>
                </Segment>
            </DimmerDimmable>

            <Chart
                height={230}
                options={{
                    chart: {
                        type: 'line',
                        zoom: {
                            enabled: false,
                        },
                        animations: {
                            animateGradually: {
                                enabled: false,
                            },
                        },
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    markers: {
                        size: 4,
                    },
                    stroke: {
                        curve: 'straight',
                        width: 2.5,
                    },
                    title: {
                        text: timeSeriesData[selectedMode].title,
                        align: 'left',
                        style: {
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: 'grey',
                        },
                    },
                    colors: [timeSeriesData[selectedMode].color],
                    xaxis: {
                        type: 'datetime',
                        labels: {
                            format: 'dd MMM',
                            formatter: function (val) {
                                return new Date(val).toLocaleDateString('pt', {
                                    day: '2-digit',
                                    month: 'short',
                                });
                            },
                        },
                    },
                }}
                series={[
                    {
                        name: 'Mensagens',
                        data: timeSeriesData[selectedMode].data as any,
                    },
                ]}
            />
        </Segment>
    );
};

export default MessagesTimeSeries;
